import type { ContourProps } from './contour-definitions';
import type { Point, PointArray } from './types/contour-definition-array-type';

import { ContourBase, ContourLabel, ContourType } from './contour-definitions';
import { processPoints } from './contour-processing/contourProcessing';

import { getPointPointDistance } from './contour-processing/getPointPointDistance';
import { snapPolyLineToSubPixelGrid } from './contour-processing/snapPolyLineToSubPixelGrid';

export class LineContour extends ContourBase {
  private _distance: number = 0;
  private _distanceRealWorldValue: number = 0;
  private _needDistanceRecalculation: boolean = true;

  constructor(
    props: Partial<ContourProps> & Pick<ContourProps, 'type' | 'label'>,
    polygon: PointArray = [],
  ) {
    super(props);
    this.setPolygonScaled(polygon);

    if (
      props.type !== ContourType.LineContour
      && props.label !== ContourLabel.AnnotationArrow
    ) {
      throw new Error('LineContour - Invalid contour type');
    }
  }

  public isValid() {
    return (
      this._polygon.length === 2
      && this._polygon[0].x !== this._polygon[1].x
      && this._polygon[0].y !== this._polygon[1].y
    );
  }

  public setPolygon(polygon: PointArray) {
    this.resetDistanceCalculation();
    if (!polygon || polygon.length < 2) {
      this._polygon = [];
      return;
    }
    this._polygon = polygon;
  }

  public setPolygonScaled(polygon: PointArray) {
    let importPolygon = polygon;
    importPolygon = snapPolyLineToSubPixelGrid(importPolygon, false);

    this._polygon = [];
    this.resetDistanceCalculation();

    this._polygon = Array.from({ length: importPolygon.length });
    for (let i = 0; i < importPolygon.length; i++) {
      this._polygon[i] = {
        x: importPolygon[i].x * ContourBase.subMatrix,
        y: importPolygon[i].y * ContourBase.subMatrix,
      };
    }
  }

  public getPolygonScaled() {
    const polygon = Array.from<Point>({ length: this._polygon.length });
    for (let i = 0; i < polygon.length; i++) {
      polygon[i] = {
        x: this._polygon[i].x / ContourBase.subMatrix,
        y: this._polygon[i].y / ContourBase.subMatrix,
      };
    }
    return polygon;
  }

  public getDensePolygon() {
    const densePolygon = processPoints(
      this.getPolygonScaled(),
      false,
      this.getImageWidth(),
      this.getImageHeight(),
    );
    for (let i = 0; i < densePolygon.length; i++) {
      densePolygon[i].x *= ContourBase.subMatrix;
      densePolygon[i].y *= ContourBase.subMatrix;
    }
    return densePolygon;
  }

  public getCentroid() {
    return {
      x: Math.abs((this._polygon[0].x + this._polygon[1].x) / 2),
      y: Math.abs((this._polygon[0].y + this._polygon[1].y) / 2),
    };
  }

  public getCentroidScaled() {
    return {
      x: Math.abs(
        (this._polygon[0].x + this._polygon[1].x) / 2 / ContourBase.subMatrix,
      ),
      y: Math.abs(
        (this._polygon[0].y + this._polygon[1].y) / 2 / ContourBase.subMatrix,
      ),
    };
  }

  public getDistance() {
    if (this._needDistanceRecalculation) {
      this.calculateDistance();
    }
    return this._distance;
  }

  public getDistanceRealWorldvalue() {
    if (this._needDistanceRecalculation) {
      this.calculateDistance();
    }
    return this._distanceRealWorldValue;
  }

  private resetDistanceCalculation() {
    this._distance = 0;
    this._distanceRealWorldValue = 0;
    this._needDistanceRecalculation = true;
  }

  private calculateDistance() {
    if (this._polygon.length < 2) {
      this._distance = 0;
      this._needDistanceRecalculation = true;
      return;
    }

    this._distance = getPointPointDistance(this._polygon[0], this._polygon[1]);
    const pixelSpacing = this.getPixelSpacing();
    this._distanceRealWorldValue = getPointPointDistance(
      this._polygon[0],
      this._polygon[1],
      // DICOM first value is row spacing, second is column spacing
      pixelSpacing[1] / ContourBase.subMatrix,
      pixelSpacing[0] / ContourBase.subMatrix,
    );
    this._needDistanceRecalculation = false;
  }
}
